import React from "react";

import {
  KintoneExportErrorDetail,
  KintoneExportReport,
  KintoneExportSetting,
  statusTextForConsole,
} from "@graffer-inc/cloud-gov-core";
import {
  StatusTag,
  Table,
  TableBody,
  TableHeader,
  TableRow,
  Td,
  Th,
  SecondaryButton,
  DefaultDialog,
  Spinner,
} from "@graffer-inc/console-design-kit";

import { useAuthContextV2 } from "../../../auth-state";
import { unixSecToTimeString } from "../../../utils/date-utils";

type Props = {
  kintoneExportReport: KintoneExportReport;
  kintoneExportSetting: KintoneExportSetting;
  kintoneExportErrorDetails?: KintoneExportErrorDetail[];
  reExportErrorSubmission: (settingId: string, submissionIds: string[]) => void;
  isReExporting: boolean;
};

export const KintoneExportReportDetailContent: React.FC<Props> = ({
  kintoneExportReport,
  kintoneExportSetting,
  kintoneExportErrorDetails,
  reExportErrorSubmission,
  isReExporting,
}) => {
  return (
    <>
      <Title
        name={`実行詳細#${kintoneExportReport.id}`}
        startedAtUnixSecond={kintoneExportReport.startedAtUnixSecond}
      />
      <hr />
      <Content
        kintoneExportReport={kintoneExportReport}
        kintoneExportSetting={kintoneExportSetting}
        kintoneExportErrorDetails={kintoneExportErrorDetails}
        reExportErrorSubmission={reExportErrorSubmission}
        isReExporting={isReExporting}
      />
    </>
  );
};

const Title: React.FC<{
  name: string;
  startedAtUnixSecond: KintoneExportReport["startedAtUnixSecond"];
}> = ({ name, startedAtUnixSecond }) => {
  return (
    <div className="columns is-vcentered">
      <div className="column has-text-left">
        <h1 className="title">{name}</h1>
        <p>{`実行日時：${unixSecToTimeString(startedAtUnixSecond)}`}</p>
      </div>
    </div>
  );
};

const Content: React.FC<Props> = ({
  kintoneExportReport,
  kintoneExportErrorDetails,
  reExportErrorSubmission,
  isReExporting,
}) => {
  const { auth } = useAuthContextV2();
  const [isOpen, setIsOpen] = React.useState(false);

  const [selectedDetail, setSelectedDetail] = React.useState<string | null>(
    null,
  );

  const apiErrorMap: Record<string, any> = React.useMemo(() => {
    if (!kintoneExportErrorDetails) {
      return {};
    }

    return kintoneExportErrorDetails.reduce(
      (acc, detail) => {
        acc[detail.smartApplySubmissionId] = detail.errors;
        return acc;
      },
      {} as Record<string, any>,
    );
  }, [kintoneExportErrorDetails]);

  return (
    <section>
      <div className="columns">
        <div className="column">
          <p>{summarizeReport(kintoneExportReport)}</p>
          {auth.isServiceProviderAccount() && (
            <p>{`grafferTraceId: ${kintoneExportReport.grafferTraceId}`}</p>
          )}
        </div>
        {kintoneExportReport.details.some((detail) => detail.errorMessage) && (
          <div className="column has-text-right">
            <SecondaryButton
              text="連携に失敗した申請を再実行する"
              onClick={() => {
                setIsOpen(true);
              }}
            />
          </div>
        )}
        {isOpen && (
          <DefaultDialog
            title="連携に失敗した申請を再実行しますか？"
            contents={
              <div>
                <p>
                  連携に失敗した
                  {
                    kintoneExportReport.details.filter(
                      (detail) => detail.errorMessage,
                    ).length
                  }
                  件の申請の連携を再実行します。
                </p>
                <p>
                  エラーの詳細を確認し、kintone
                  アプリ側の設定を見直した上で再実行してください。
                </p>
              </div>
            }
            proceedText="再実行"
            cancelText="キャンセル"
            onCancel={() => setIsOpen(false)}
            onProceed={() => {
              setIsOpen(false);
              reExportErrorSubmission(
                kintoneExportReport.exportSettingId,
                kintoneExportReport.details
                  .filter((detail) => detail.errorMessage)
                  .map((detail) => detail.smartApplySubmissionId),
              );
            }}
          />
        )}
      </div>
      {kintoneExportReport.details.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <Th>ステータス</Th>
              <Th>申請 ID</Th>
              <Th>詳細</Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            {kintoneExportReport.details.map((detail) => {
              const { result, message } = summarizeDetail(detail);
              return (
                <TableRow key={detail.smartApplySubmissionId}>
                  <Td>
                    <StatusTag type={result}>{result}</StatusTag>
                  </Td>
                  <Td>{detail.smartApplySubmissionId} </Td>
                  <Td>
                    {message}
                    {apiErrorMap[detail.smartApplySubmissionId] && (
                      <button
                        style={{ textDecoration: "underline" }}
                        onClick={() => {
                          setSelectedDetail(
                            detail.smartApplySubmissionId === selectedDetail
                              ? null
                              : detail.smartApplySubmissionId,
                          );
                        }}
                      >
                        <span className="icon">
                          <i className="fas fa-info-circle"></i>
                        </span>
                        エラー詳細
                      </button>
                    )}
                    {selectedDetail === detail.smartApplySubmissionId && (
                      <div className="my-4 content">
                        {apiErrorMap[detail.smartApplySubmissionId].map(
                          (error: string) => (
                            <code
                              style={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-all",
                                display: "block",
                              }}
                              key={error}
                            >
                              {`${JSON.stringify(JSON.parse(error), null, 2)}`}
                            </code>
                          ),
                        )}
                        <p className="mt-4">
                          ※ エラー詳細は、発生から30日間のみ表示されます。
                        </p>
                      </div>
                    )}
                  </Td>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <Spinner loading={isReExporting} />
    </section>
  );
};

const summarizeReport = ({
  errorMessage,
  details,
}: KintoneExportReport): string => {
  if (errorMessage) {
    return `連携を実行できませんでした: ${errorMessage}`;
  }

  if (details.length === 0) {
    return "連携対象の申請データがありませんでした。";
  }

  const succeeded = details.filter(({ kintoneRecordId }) => !!kintoneRecordId);
  return `${details.length}件中、${succeeded.length}件の申請データを連携しました。`;
};

type SummarizeDetailOutput = {
  result: "SUCCESS" | "ERROR" | "WARN";
  message: string;
};

const summarizeDetail = ({
  kintoneRecordId,
  errorMessage,
  existingRecord,
  aplStatusUpdate,
  skippedByAplStatus,
}: KintoneExportReport["details"][number]): SummarizeDetailOutput => {
  if (errorMessage) {
    return { result: "ERROR", message: errorMessage };
  }

  if (existingRecord) {
    return {
      result: "WARN",
      message: `申請データは kintone レコード ${existingRecord.kintoneRecordId} へ連携済みだったので、処理をスキップしました。`,
    };
  }

  if (skippedByAplStatus) {
    return {
      result: "WARN",
      message: `申請データのステータスが ${statusTextForConsole(
        skippedByAplStatus,
      )} だったので、処理をスキップしました。`,
    };
  }

  const successResult: SummarizeDetailOutput = {
    result: "SUCCESS",
    message: `申請データを kintone レコード ${kintoneRecordId} へ連携しました。`,
  };

  if (aplStatusUpdate) {
    successResult.message = `ステータスを${statusTextForConsole(
      aplStatusUpdate.before,
    )}から${statusTextForConsole(aplStatusUpdate.after)}へ更新して、${
      successResult.message
    }`;
  }

  return successResult;
};
